import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  BaseCompanyGroupIdProps,
  BaseCompanyIdProps,
} from "@custom-types/base-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  AddGroupProps,
  RemoveMemberProps,
  RemoveMemberResult,
  UpdateGroupDetailsProps,
  UpdateMemberRoleInGroupProps,
  UpdateMemberRoleInGroupResult,
} from "@store/groups/groups-slice-types";
import { RootState } from "@store/store-helper";

/**
 * Update a member role in the group using the backend,
 * and then update it in group in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateMemberRoleInGroup = createAsyncThunk<
  UpdateMemberRoleInGroupResult,
  UpdateMemberRoleInGroupProps
>(
  "groups/updateMemberRoleInGroup",
  async ({ companyId, groupId, role, userIdentity }) => {
    await faroCoreApiClient.groups.updateGroupMemberRole({
      companyId,
      groupId,
      userIdentity,
      payload: { role },
    });

    return {
      groupId,
      role,
      userIdentity,
    };
  }
);

/**
 * Fetches groups from the backend so they can be put into the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchGroups = createAsyncThunk<
  FaroCoreApiTypes.GroupSummaryResponse[],
  BaseCompanyIdProps
>("groups/fetchGroups", async ({ companyId }) => {
  return await faroCoreApiClient.groups.getGroups({ companyId });
});

/** Fetches the details of a group */
/**
 * Fetches the details of a group
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchGroupDetails = createAsyncThunk<
  FaroCoreApiTypes.GroupDetailResponse,
  BaseCompanyGroupIdProps
>("groups/fetchGroupDetails", async ({ companyId, groupId }) => {
  return await faroCoreApiClient.groups.getGroupById({
    companyId,
    groupId,
  });
});

/**
 * Creates a group in the backend and adds it to the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const addGroup = createAsyncThunk<
  FaroCoreApiTypes.GroupSummaryResponse,
  AddGroupProps
>(
  "groups/addGroup",
  async ({ companyId, groupName, selectedGroupManagerIds }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { message, ...group } = await faroCoreApiClient.groups.addGroup({
      companyId,
      payload: {
        name: groupName,
        assignments: [
          {
            identities: selectedGroupManagerIds,
            role: "COMPANY_MANAGER",
          },
        ],
      },
    });
    return group;
  }
);

/**
 * Update group details
 * @throws {Error} if no companyId or groupId exist to update the group details
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateGroupDetails = createAsyncThunk<
  FaroCoreApiTypes.LegacyGroupDetailResponse,
  UpdateGroupDetailsProps,
  {
    state: RootState;
  }
>("groups/updateGroupDetails", async ({ groupId, payload }, { getState }) => {
  const {
    groups: { selectedGroupId },
    sdbCompany: { selectedSdbCompanyId },
  } = getState();

  /**
   * The ID of the group that is going to be updated.
   * If groupId is not provided, the selectedGroupId from store is selected
   */
  const updatingGroupId = groupId || selectedGroupId;

  if (!selectedSdbCompanyId || !updatingGroupId) {
    throw new Error(
      "No companyId or groupId exist to was given to updateGroupDetails"
    );
  }

  return await faroCoreApiClient.groups.updateGroupDetails({
    companyId: selectedSdbCompanyId,
    groupId: updatingGroupId,
    payload,
  });
});

/**
 * Remove a member from the group using the backend,
 * and then removes it from group in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMemberFromGroup = createAsyncThunk<
  RemoveMemberResult,
  RemoveMemberProps
>(
  "groups/removeMemberFromGroup",
  async ({ companyId, groupId, member, onGroupRemoved }) => {
    await faroCoreApiClient.groups.removeMemberFromGroup({
      companyId,
      groupId,
      userIdentity: member.identity,
    });
    return {
      groupId,
      companyId,
      member,
      onGroupRemoved,
    };
  }
);
